import ClearIcon from '@mui/icons-material/Clear'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { Box, Grid2, InputAdornment, Stack, Typography } from '@mui/material'
import TextField from '@mui/material/TextField'
import { useCallback, useEffect, useState } from 'react'
import { Help_And_Instruction_Link_Bool_Exp, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
  useTableSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import { HelpInstructionsConfigGrid } from 'src/screens/administration/configuration-management/help-instructions-configuration/HelpInstructionsConfigGrid'
import { queryHelpInstructionLinks } from 'src/screens/administration/configuration-management/help-instructions-configuration/helpInstructionsConfigQueries'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
} from 'src/shared/constants/table-settings-constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { SecondaryConfirmationModalDialog } from 'src/shared/modal-dialog/SecondaryConfirmationModalDialog'
import { useDebounce } from 'src/shared/utils/hooks/search-field-debounce-hook'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import { useClient } from 'urql'
import { deleteHelpInstruction } from './HelpInstructionsGridUtils'

const HELP_INSTRUCTION_TABLE_NAME = 'HELP_INSTRUCTION_CONFIG'

type HelpInstructionFilters = SpecificConfigFilter<'HELP_INSTRUCTION'>

const defaultHelpInstructionTableSettings: ConfigurationTablePageSortSettings = {
  ...DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
}

const defaultHelpInstructionFilterValues: HelpInstructionFilters = {
  helpInstructionSearchFilter: '',
  helpInstructionSearchFilterTyped: '',
}

export const HelpInstructionsConfigurationIndexPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()
  const debounceHelpInstructionFilter = useDebounce()

  // grid and filter settings
  const [gridDataSettings, setGridDataSettings] = useState<ConfigurationGridDataSettings>(
    DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  )
  const [tableSettings, setTableSettings] = useTableSettingsFromLocalStorage(
    HELP_INSTRUCTION_TABLE_NAME,
    defaultHelpInstructionTableSettings,
  )
  const [helpInstructionFilters, setHelpInstructionFilters] = useFilterSettingsFromLocalStorage(
    defaultHelpInstructionFilterValues,
  )

  // delete modal state
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [deleteModalLoading, setDeleteModalLoading] = useState<boolean>(false)
  const [helpInstructionIdToBeDeleted, setHelpInstructionIdToBeDeleted] = useState<number | null>(null)

  // table state
  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const helpInstructionsConfigCondition: Help_And_Instruction_Link_Bool_Exp = { _and: [] }
    if (helpInstructionFilters.helpInstructionSearchFilter.length > 0) {
      helpInstructionsConfigCondition._and?.push({
        _or: [
          {
            labels: {
              _cast: {
                String: {
                  _ilike: `%${helpInstructionFilters.helpInstructionSearchFilter}%`,
                },
              },
            },
          },
        ],
      })
    }

    const { data, error } = await urqlClient
      .query<
        {
          help_and_instruction_link: Query_Root['help_and_instruction_link']
          help_and_instruction_link_aggregate: Query_Root['help_and_instruction_link_aggregate']
        },
        {
          helpInstructionsConfigCondition: Help_And_Instruction_Link_Bool_Exp
          offset: number
          limit: number
        }
      >(queryHelpInstructionLinks, {
        helpInstructionsConfigCondition,
        offset,
        limit,
      })
      .toPromise()

    if (error || !data?.help_and_instruction_link || !data?.help_and_instruction_link_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.help_and_instruction_link.map((hai) => ({
      id: hai.id,
      label_locale: hai.help_and_instruction_name_locale,
      link_locale: hai.links[locale],
      location_hints: hai.location_hints,
    }))
    const fetchedRowsCount = data.help_and_instruction_link_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [helpInstructionFilters.helpInstructionSearchFilter, tableSettings.paginationModel, locale, urqlClient])

  const initRows = useCallback(() => {
    setGridDataSettings((prevGridData) => ({
      ...prevGridData,
      gridLoading: true,
    }))

    fetchRows()
      .then((res) => {
        setGridDataSettings({
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
          gridLoading: false,
        })
      })
      .catch((_) => {
        notificationService.operationFailed()
        setGridDataSettings((prevGridData) => ({
          ...prevGridData,
          gridLoading: false,
        }))
      })
  }, [fetchRows, notificationService])

  // filter handlers
  const handleChangeSearchFilterValue = useCallback(
    (newValue: string) => {
      setHelpInstructionFilters({
        ...helpInstructionFilters,
        helpInstructionSearchFilterTyped: newValue,
      })

      debounceHelpInstructionFilter(() => {
        setGridDataSettings((prevGridData) => ({
          ...prevGridData,
          gridLoading: true,
        }))
        setTableSettings({
          ...tableSettings,
          paginationModel: { ...tableSettings.paginationModel, page: 0 },
        })

        setHelpInstructionFilters({
          ...helpInstructionFilters,
          helpInstructionSearchFilter: newValue,
          helpInstructionSearchFilterTyped: newValue,
        })
      }, 500)
    },
    [setHelpInstructionFilters, helpInstructionFilters, debounceHelpInstructionFilter, setTableSettings, tableSettings],
  )

  const handleClearSearchFilter = useCallback(() => {
    handleChangeSearchFilterValue('')
  }, [handleChangeSearchFilterValue])

  // delete modal handlers
  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmationOpen(false)
  }, [])

  const handleDeleteConfirmed = useCallback(() => {
    if (!helpInstructionIdToBeDeleted) {
      notificationService.operationFailed()
      return
    }

    setDeleteModalLoading(true)
    deleteHelpInstruction(urqlClient)(helpInstructionIdToBeDeleted)
      .then(() => {
        notificationService.deleteSuccessful()
        setDeleteModalLoading(false)
        setHelpInstructionIdToBeDeleted(null)
        initRows()
        setDeleteConfirmationOpen(false)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [helpInstructionIdToBeDeleted, initRows, notificationService, urqlClient])

  useEffect(() => {
    initRows()
  }, [initRows])

  return (
    <>
      <ScreenLayout
        hasSecondLevelNavigation={false}
        title={`${getMessage('label.navigation.configuration.help.instructions')} ${getMessage('label.configuration')}`}
      >
        <PageLayout>
          <Stack spacing={5}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                padding: 2,
              }}
            >
              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <TextField
                    label={getMessage('label.configuration.help.instructions.grid.search.field.label')}
                    value={helpInstructionFilters.helpInstructionSearchFilterTyped}
                    onChange={(e) => handleChangeSearchFilterValue(e.target.value)}
                    variant="filled"
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Stack direction="row" spacing={1}>
                              {helpInstructionFilters.helpInstructionSearchFilterTyped.length > 0 && (
                                <ClearIcon sx={{ cursor: 'pointer' }} onClick={handleClearSearchFilter} />
                              )}
                              <SearchOutlinedIcon />
                            </Stack>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid2>
              </Grid2>
            </Box>
            <HelpInstructionsConfigGrid
              dataSettings={gridDataSettings}
              tableSettings={tableSettings}
              setTableSettings={setTableSettings}
              setDeleteConfirmationOpen={setDeleteConfirmationOpen}
              setHelpInstructionIdToBeDeleted={setHelpInstructionIdToBeDeleted}
            />
          </Stack>
        </PageLayout>
      </ScreenLayout>

      <SecondaryConfirmationModalDialog
        open={deleteConfirmationOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteConfirmed}
        loading={deleteModalLoading}
        buttonColor="error"
      >
        <Box paddingRight={1}>
          <Typography variant="subtitle1">
            {getMessage('label.configuration.help.and.instructions.grid.delete.confirm.body')}
          </Typography>
        </Box>
      </SecondaryConfirmationModalDialog>
    </>
  )
}
